import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiAward, FiUsers, FiTrendingUp, FiShield, FiTarget, FiHeart, FiArrowRight, FiStar } = FiIcons;

const AboutPage = () => {
  const stats = [
    { number: '500+', label: 'Successful Implementations' },
    { number: '98%', label: 'Client Satisfaction Rate' },
    { number: '50+', label: 'Certified Experts' },
    { number: '24/7', label: 'Support Availability' }
  ];

  const team = [
    {
      name: 'Michael Rodriguez',
      role: 'Founder & CEO',
      bio: 'Former Microsoft Principal Architect with 15+ years in data governance. Led Purview implementations for Fortune 100 companies.',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop&crop=face',
      certifications: ['Microsoft MVP', 'Azure Solutions Architect Expert', 'Data Engineer Associate']
    },
    {
      name: 'Sarah Chen',
      role: 'Chief Technology Officer',
      bio: 'Data governance expert with deep Microsoft ecosystem knowledge. Specializes in large-scale Purview deployments.',
      image: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=400&h=400&fit=crop&crop=face',
      certifications: ['Microsoft Certified Trainer', 'Azure Data Scientist Associate', 'Security Engineer Associate']
    },
    {
      name: 'David Thompson',
      role: 'Head of Implementation',
      bio: 'Compliance specialist with expertise in GDPR, HIPAA, and SOX. Ensures all implementations meet regulatory requirements.',
      image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=400&fit=crop&crop=face',
      certifications: ['Certified Information Systems Auditor', 'Privacy Professional', 'Risk Management Professional']
    }
  ];

  const values = [
    { icon: FiTarget, title: 'Results-Driven', description: 'We focus on delivering measurable outcomes that drive your business forward.' },
    { icon: FiShield, title: 'Security First', description: 'Your data security and compliance are our top priorities in every project.' },
    { icon: FiUsers, title: 'Client Partnership', description: 'We work as an extension of your team, ensuring your success is our success.' },
    { icon: FiHeart, title: 'Continuous Innovation', description: 'We stay ahead of the curve with the latest Microsoft Purview features and best practices.' }
  ];

  const milestones = [
    { year: '2018', event: 'Founded with a vision to simplify data governance' },
    { year: '2019', event: 'First 50 successful Microsoft Purview implementations' },
    { year: '2021', event: 'Achieved Microsoft Gold Partner status' },
    { year: '2022', event: 'Expanded to serve Fortune 500 companies globally' },
    { year: '2023', event: 'Launched 24/7 support program' },
    { year: '2024', event: 'Reached 500+ successful implementations milestone' }
  ];

  return (
    <div className="py-16">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-900 to-purple-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.div 
              initial={{ opacity: 0, y: 20 }} 
              animate={{ opacity: 1, y: 0 }} 
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-5xl font-bold mb-6">
                The Microsoft Purview Experts You Can Trust
              </h1>
              <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
                Founded by former Microsoft architects, we've helped 500+ organizations achieve data governance excellence with proven methodologies and unmatched expertise.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {stats.map((stat, index) => (
              <motion.div 
                key={index} 
                initial={{ opacity: 0, y: 20 }} 
                whileInView={{ opacity: 1, y: 0 }} 
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="p-6"
              >
                <div className="text-4xl font-bold text-blue-600 mb-2">{stat.number}</div>
                <div className="text-gray-600">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div 
              initial={{ opacity: 0, x: -20 }} 
              whileInView={{ opacity: 1, x: 0 }} 
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Story</h2>
              <div className="space-y-4 text-lg text-gray-700">
                <p>
                  In 2018, our founder Michael Rodriguez was working as a Principal Architect at Microsoft when he witnessed countless organizations struggle with data governance challenges. Despite having powerful tools like Microsoft Purview, companies were failing to implement them effectively.
                </p>
                <p>
                  The problem wasn't the technology—it was the lack of expertise and proven methodologies. Companies were losing millions in compliance fines and data breaches because they couldn't properly govern their data.
                </p>
                <p>
                  That's when Michael decided to leave Microsoft and start YourPurviewPartner. Our mission: to make enterprise-grade data governance accessible to every organization, regardless of size or industry.
                </p>
                <p className="text-blue-600 font-semibold">
                  Today, we're proud to have helped over 500 organizations transform their data governance and achieve 100% compliance with our proven methodologies.
                </p>
              </div>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, x: 20 }} 
              whileInView={{ opacity: 1, x: 0 }} 
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <img 
                src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=600&h=400&fit=crop" 
                alt="Team collaboration" 
                className="rounded-lg shadow-xl" 
              />
              <div className="absolute inset-0 bg-blue-600 opacity-20 rounded-lg"></div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Meet Our Leadership Team</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Led by former Microsoft architects and data governance experts with decades of combined experience
            </p>
          </div>
          <div className="grid lg:grid-cols-3 gap-8">
            {team.map((member, index) => (
              <motion.div 
                key={index} 
                initial={{ opacity: 0, y: 20 }} 
                whileInView={{ opacity: 1, y: 0 }} 
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-gray-50 p-8 rounded-lg text-center"
              >
                <img 
                  src={member.image} 
                  alt={member.name} 
                  className="w-32 h-32 rounded-full mx-auto mb-6 object-cover" 
                />
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{member.name}</h3>
                <p className="text-blue-600 font-semibold mb-4">{member.role}</p>
                <p className="text-gray-600 mb-6">{member.bio}</p>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Certifications:</h4>
                  <div className="space-y-1">
                    {member.certifications.map((cert, idx) => (
                      <div key={idx} className="text-sm text-gray-600 flex items-center justify-center">
                        <SafeIcon icon={FiAward} className="text-yellow-500 mr-2" />
                        {cert}
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 bg-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Core Values</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              These principles guide everything we do and ensure we deliver exceptional results for our clients
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div 
                key={index} 
                initial={{ opacity: 0, y: 20 }} 
                whileInView={{ opacity: 1, y: 0 }} 
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white p-6 rounded-lg shadow-lg text-center hover:shadow-xl transition-shadow"
              >
                <SafeIcon icon={value.icon} className="h-12 w-12 text-blue-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Journey</h2>
            <p className="text-xl text-gray-600">
              From startup to industry leader - here's how we've grown to serve clients worldwide
            </p>
          </div>
          <div className="space-y-8">
            {milestones.map((milestone, index) => (
              <motion.div 
                key={index} 
                initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }} 
                whileInView={{ opacity: 1, x: 0 }} 
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`flex items-center ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}
              >
                <div className="flex-1 text-center">
                  <div className="bg-blue-600 text-white px-4 py-2 rounded-full inline-block font-bold text-lg">
                    {milestone.year}
                  </div>
                </div>
                <div className="w-4 h-4 bg-blue-600 rounded-full mx-8 flex-shrink-0"></div>
                <div className="flex-1">
                  <p className="text-lg text-gray-700">{milestone.event}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-6">Ready to Work with the Experts?</h2>
          <p className="text-xl mb-8 text-blue-100">
            Join the 500+ companies who trust us with their Microsoft Purview implementations
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/purview-audit" 
              className="inline-flex items-center bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-lg text-lg font-bold transition-all transform hover:scale-105"
            >
              Get Purview Assessment
              <SafeIcon icon={FiArrowRight} className="ml-2" />
            </Link>
            <Link 
              to="/contact" 
              className="inline-flex items-center bg-white text-blue-600 px-8 py-4 rounded-lg text-lg font-bold hover:bg-gray-100 transition-colors"
            >
              Meet Our Team
            </Link>
          </div>
          <p className="text-sm mt-6 text-blue-200">
            ✓ Former Microsoft architects ✓ 500+ implementations ✓ 98% satisfaction rate
          </p>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;